// Pure date maths for the calendar grid. No React, no DOM: every function
// here takes plain Dates and returns plain Dates or Date-shaped records, so
// it can be tested directly instead of through rendering.
//
// Everything reads and writes browser-local time via the local Date getters
// (getFullYear/getMonth/getDate/...) and the local Date constructor -- that
// is what the operator sees on screen. The one place that matters is
// `rangeFor`, whose `from`/`to` are the instants the API receives (encoded
// as UTC on the wire): a bound built from local midnight, sent through
// toISOString(), lands on a different calendar day depending on the
// browser's offset from UTC. See test/calendar-grid.test.ts for the
// zone-aware coverage of that boundary.

export type DayCell = { date: Date; inCurrentPeriod: boolean; isToday: boolean };

const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// JS Date.getDay(): Sunday = 0 ... Saturday = 6. The grid's weeks start on
// Monday, so re-index with Monday = 0 ... Sunday = 6.
const mondayIndex = (jsDay: number): number => (jsDay + 6) % 7;

// Midnight of the Monday on or before `date`, in local time.
const startOfWeek = (date: Date): Date => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - mondayIndex(start.getDay()));
  return start;
};

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Adds `months` calendar months to `date`, clamping the day-of-month to the
// target month's last day rather than letting it overflow. This is the fix
// for the classic bug: naive `setMonth` on Jan 31 + 1 month rolls over into
// March (Feb has no 31st, so JS normalizes the excess days forward), landing
// on Mar 3. Clamping instead lands on Feb 28 (or Feb 29 in a leap year).
const addMonthsClamped = (date: Date, months: number): Date => {
  const day = date.getDate();
  const firstOfTargetMonth = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfTargetMonth = new Date(
    firstOfTargetMonth.getFullYear(),
    firstOfTargetMonth.getMonth() + 1,
    0,
  ).getDate();
  const result = new Date(firstOfTargetMonth);
  result.setDate(Math.min(day, lastDayOfTargetMonth));
  result.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return result;
};

const dayCell = (date: Date, inCurrentPeriod: boolean, now: Date): DayCell => ({
  date,
  inCurrentPeriod,
  isToday: isSameLocalDay(date, now),
});

/** The seven days (Monday through Sunday) of the week containing `anchor`. */
export const weekCells = (anchor: Date, now: Date): DayCell[] => {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => dayCell(addDays(start, i), true, now));
};

/**
 * The whole-week grid covering the calendar month containing `anchor`: it
 * always starts on a Monday and always ends on a Sunday, so the returned
 * length is always a multiple of 7. Days that fall outside `anchor`'s month
 * (the leading/trailing days needed to complete the first/last week) are
 * included with `inCurrentPeriod: false` rather than omitted, so the caller
 * can render a full grid without gaps.
 */
export const monthCells = (anchor: Date, now: Date): DayCell[] => {
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const gridStart = startOfWeek(monthStart);
  const gridEndExclusive = addDays(startOfWeek(monthEnd), 7);

  const cells: DayCell[] = [];
  for (let d = gridStart; d.getTime() < gridEndExclusive.getTime(); d = addDays(d, 1)) {
    cells.push(dayCell(d, d.getMonth() === anchor.getMonth() && d.getFullYear() === anchor.getFullYear(), now));
  }
  return cells;
};

/**
 * The instant range the API should be queried with for `view`, anchored on
 * `anchor`. `from` is local midnight of the first day; `to` is the final
 * millisecond of the last day (23:59:59.999 local), not that day's
 * midnight -- a `to` of the last day's midnight would exclude every post
 * scheduled that afternoon from the result.
 *
 * 'list' shares month's range: the plan and spec do not define a distinct
 * span for the list view (it is a different rendering of the same
 * scope, not a different query), so it is treated as month-scoped here.
 */
export const rangeFor = (view: 'week' | 'month' | 'list', anchor: Date): { from: Date; to: Date } => {
  const from = view === 'week' ? startOfWeek(anchor) : new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const toExclusive =
    view === 'week' ? addDays(from, 7) : new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  const to = new Date(toExclusive.getTime() - 1);
  return { from, to };
};

/** Moves `anchor` by one unit of `view` (a week, or a calendar month). */
export const shiftAnchor = (view: 'week' | 'month' | 'list', anchor: Date, delta: -1 | 1): Date => {
  if (view === 'week') {
    return addDays(anchor, delta * 7);
  }
  return addMonthsClamped(anchor, delta);
};
